import React, { useState, useEffect } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, SubTitle, Tooltip, Legend } from 'chart.js';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchAiChartData } from '../../services/analyticsApi';

// Register Chart.js components
// Subtitle: the synthetic-data note on manufacturer-level charts (options.plugins.subtitle).
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, SubTitle, Tooltip, Legend);

const CHART_PARSE_ERROR = 'The chart configuration returned by the server could not be displayed. Please try generating the chart again.';
const REQUEST_ERROR = 'The chart could not be generated because the request failed. Please try again.';
const NO_CHART_FALLBACK = 'No chart was generated for this request.';

// --- Chart configuration parsing -------------------------------------------
// The chart configuration is written by an AI model, so it is untrusted data.
// It is accepted only as a plain JSON object (or a strict-JSON string of one),
// validated, and copied. It is never evaluated as JavaScript.

// Chart.js built-in chart types (the dashboard renders with chart.js/auto).
const SUPPORTED_CHART_TYPES = new Set(['bar', 'line', 'pie', 'doughnut', 'polarArea', 'radar', 'scatter', 'bubble']);
const POINT_CHART_TYPES = new Set(['scatter', 'bubble']); // data as {x, y[, r]}; labels optional
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
// Strings shaped like function source. Chart.js never evaluates strings, but such
// values only appear when a callback was attempted, so the config is rejected.
const FUNCTION_SOURCE = /^\s*(async\s+)?(function\b|\([^)]*\)\s*=>|[A-Za-z_$][\w$]*\s*=>)/;
const MAX_DEPTH = 20;

const isPlainObject = (value) =>
    value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

// True only for JSON-like data: plain objects, arrays, strings, finite numbers, booleans, null.
const isPlainData = (value, depth = 0) => {
    if (depth > MAX_DEPTH) return false;
    if (value === null || typeof value === 'boolean') return true;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return !FUNCTION_SOURCE.test(value);
    if (Array.isArray(value)) return value.every((item) => isPlainData(item, depth + 1));
    if (isPlainObject(value)) {
        return Object.keys(value).every((key) => !UNSAFE_KEYS.has(key) && isPlainData(value[key], depth + 1));
    }
    return false; // functions, symbols, undefined, class instances
};

// A plain object, or a strict-JSON string of one. Anything else (including
// JavaScript object literals and expressions) yields null.
const parseJsonObject = (value) => {
    if (isPlainObject(value)) return value;
    if (typeof value !== 'string') return null;
    try {
        const parsed = JSON.parse(value.trim().replace(/,\s*$/, ''));
        return isPlainObject(parsed) ? parsed : null;
    } catch (_) {
        return null;
    }
};

const isLabel = (label) =>
    typeof label === 'string' || typeof label === 'number'
    || (Array.isArray(label) && label.every((line) => typeof line === 'string')); // multi-line label

const isDataPoint = (point) =>
    point === null
    || (typeof point === 'number' && Number.isFinite(point))
    || (isPlainObject(point) && Object.values(point).every(
        (v) => v === null || typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v))));

const isValidChartConfig = (chartConfig) => {
    if (!isPlainObject(chartConfig) || !isPlainData(chartConfig)) return false;
    if (!SUPPORTED_CHART_TYPES.has(chartConfig.type)) return false;
    const { data, options } = chartConfig;
    if (!isPlainObject(data)) return false;
    if (options !== undefined && !isPlainObject(options)) return false;

    const { labels, datasets } = data;
    if (labels === undefined) {
        if (!POINT_CHART_TYPES.has(chartConfig.type)) return false;
    } else if (!Array.isArray(labels) || !labels.every(isLabel)) {
        return false;
    }
    return Array.isArray(datasets) && datasets.length > 0 && datasets.every((dataset) =>
        isPlainObject(dataset)
        && (dataset.label === undefined || typeof dataset.label === 'string')
        && Array.isArray(dataset.data)
        && dataset.data.every(isDataPoint));
};

// Turns an /ai-data-provider/ chart response into a widget, or returns null if
// the configuration is not a valid, supported chart.
export const parseWidgetData = (widgetData) => {
    if (!widgetData || widgetData.type !== 'chart') return null;

    try {
        let content = parseJsonObject(widgetData.content);
        if (!content) return null;
        // Unwrap the backend's ChartOutput envelope: {"type": "chart", "content": {title, config}}
        if (isPlainObject(content.content) && content.content.config !== undefined) {
            content = content.content;
        }
        if (content.title !== undefined && content.title !== null && typeof content.title !== 'string') return null;

        const parsedConfig = parseJsonObject(content.config);
        if (!parsedConfig || !isValidChartConfig(parsedConfig)) {
            console.warn('Rejected an invalid or unsupported chart configuration from the server.');
            return null;
        }

        // Validated plain data, so a JSON round trip is an exact, unshared copy.
        const chartConfig = JSON.parse(JSON.stringify(parsedConfig));
        // Ensure options exist to prevent the undefined error, as backend no longer sends them
        if (!chartConfig.options) {
            chartConfig.options = { responsive: true, maintainAspectRatio: false };
        }

        const datasets = chartConfig.data.datasets;
        const labels = chartConfig.data.labels || [];
        const tableData = {
            headers: ['Model', ...datasets.map(dataset => dataset.label || 'Value')],
            rows: labels.map((label, index) => {
                return [label, ...datasets.map(dataset => {
                    const value = dataset.data[index];
                    return isPlainObject(value) ? JSON.stringify(value) : (value ?? null);
                })];
            })
        };

        return {
            id: widgetData.id,
            title: content.title || 'Data Visualization',
            config: chartConfig,
            tableData: tableData,
        };
    } catch (_) {
        console.warn('Rejected an unreadable chart configuration from the server.');
        return null;
    }
};


const CreateGraphModal = ({ isOpen, onClose, widgets, setWidgets, layout, setLayout }) => {
    const [prompt, setPrompt] = useState('');
    const [widgetPreview, setWidgetPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState('prompt'); // 'prompt', 'preview'
    // Shown instead of a chart: { kind: 'info' | 'error', text }
    const [message, setMessage] = useState(null);

    // Reset state when modal is closed or reopened
    useEffect(() => {
        if (isOpen) {
            setStep('prompt');
            setWidgetPreview(null);
            setMessage(null);
            setPrompt('');
        }
    }, [isOpen]);

    // Simulates calling an AI API to get chart data
    const handleGenerateWidget = () => {
        if (!prompt.trim()) return; // Don't generate if prompt is empty
        setIsLoading(true);
        setWidgetPreview(null);
        setMessage(null);

        fetchAiChartData(prompt)
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(apiResponse => {
            const responseWithId = {
                id: `widget-${Date.now()}`,
                ...apiResponse 
            };

            if (apiResponse.type === 'chart') {
                const parsedWidget = parseWidgetData(responseWithId);
                setWidgetPreview(parsedWidget);
                if (!parsedWidget) setMessage({ kind: 'error', text: CHART_PARSE_ERROR });
            } else {
                // 'language' responses explain why no chart was produced, e.g. the data
                // was only partially returned or is not available.
                setMessage({ kind: 'info', text: apiResponse.content || NO_CHART_FALLBACK });
            }
            setStep('preview');
        })
        .catch(error => {
            console.error('There was a problem with the fetch operation:', error);
            setMessage({ kind: 'error', text: REQUEST_ERROR });
            setStep('preview');
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    // Adds the accepted widget to the main dashboard
    const handleAcceptWidget = () => {
        if (!widgetPreview) return;

        const newLayoutItem = {
            i: widgetPreview.id,
            x: (layout.length * 6) % 12, 
            y: Infinity, 
            w: 6,
            h: 5, 
            minW: 4,
            minH: 4,
        };

        setWidgets([...widgets, widgetPreview]);
        setLayout([...layout, newLayoutItem]);
        handleClose();
    };
    
    // Reset state and close modal
    const handleClose = () => {
        onClose();
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 bg-primary bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <motion.div
                        className="bg-white/90 w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden"
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="p-8">
                            <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Create a New Insight</h2>
                            <p className="text-center text-gray-500 mb-8">Describe the data you want to visualize.</p>

                            {/* --- Prompt Input Area --- */}
                            <div className="flex items-center space-x-4 mb-8">
                                <textarea
                                    className="w-full px-4 py-3 text-gray-700 bg-white-100 border-2 border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white transition"
                                    rows="2"
                                    placeholder="e.g., 'Show top 5 car models with the highest warranty claims this year'"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    disabled={isLoading}
                                />
                                <motion.button
                                    onClick={handleGenerateWidget}
                                    className="px-6 py-4 bg-blue-500 text-white font-semibold rounded-lg shadow-lg disabled:bg-blue-300 disabled:cursor-not-allowed"
                                    disabled={isLoading}
                                    whileHover={{ scale: isLoading ? 1 : 1.05 }}
                                    whileTap={{ scale: isLoading ? 1 : 0.95 }}
                                >
                                    {isLoading ? 'Generating...' : 'Generate'}
                                </motion.button>
                            </div>

                            {/* --- Preview Section --- */}
                            <AnimatePresence>
                                {(isLoading || widgetPreview || message) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <div className="border-t-2 border-gray-200 pt-6">
                                            {isLoading && (
                                                <div className="flex flex-col items-center justify-center h-80">
                                                    <motion.div
                                                        className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    />
                                                    <p className="mt-4 text-gray-600">AI is analyzing your request...</p>
                                                </div>
                                            )}
                                            {step === 'preview' && !isLoading && message && (
                                                <div
                                                    role={message.kind === 'error' ? 'alert' : 'status'}
                                                    className={`p-4 rounded-lg border ${message.kind === 'error'
                                                        ? 'bg-red-50 border-red-200 text-red-800'
                                                        : 'bg-amber-50 border-amber-200 text-amber-900'}`}
                                                >
                                                    <p className="font-semibold mb-1">
                                                        {message.kind === 'error' ? 'Chart could not be generated' : 'No chart was generated'}
                                                    </p>
                                                    <p className="whitespace-pre-line">{message.text}</p>
                                                </div>
                                            )}
                                            {step === 'preview' && widgetPreview && widgetPreview.config && (
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">{widgetPreview.title}</h3>
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[400px]">
                                                        {/* Chart Preview */}
                                                        <div className="bg-white/80 p-4 rounded-lg shadow-inner">
                                                            <Chart type={widgetPreview.config.type} options={widgetPreview.config.options} data={widgetPreview.config.data} />
                                                        </div>
                                                        {/* Table Preview */}
                                                        <div className="bg-white/80 p-4 rounded-lg shadow-inner overflow-y-auto">
                                                            <table className="w-full text-left">
                                                                <thead className="sticky top-0 bg-gray-100">
                                                                    <tr>
                                                                        {widgetPreview.tableData.headers.map(header => (
                                                                            <th key={header} className="p-3 text-sm font-semibold text-gray-600">{header}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {widgetPreview.tableData.rows.map((row, index) => (
                                                                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                                                                            {row.map((cell, cellIndex) => (
                                                                                <td key={cellIndex} className="p-3 text-gray-700">{cell}</td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        {/* --- Action Buttons --- */}
                        <div className="bg-gray-50/70 p-4 flex justify-end items-center space-x-4">
                            {step === 'preview' && !isLoading && (
                                <motion.div className="flex space-x-4" initial={{opacity: 0}} animate={{opacity: 1}}>
                                     <button
                                        onClick={handleGenerateWidget}
                                        className="font-semibold text-gray-600 hover:text-blue-600 px-4 py-2 rounded-md"
                                    >
                                        Retry
                                    </button>
                                    {widgetPreview && (
                                        <motion.button
                                            onClick={handleAcceptWidget}
                                            className="px-6 py-2 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            Accept and Add
                                        </motion.button>
                                    )}
                                </motion.div>
                            )}
                            <button onClick={handleClose} className="font-semibold text-gray-500 hover:text-gray-800 px-4 py-2">Cancel</button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CreateGraphModal;